import { useRef } from "react";
import { AsyncEntityPicker } from "../work-type-shared/async-entity-picker";
import { WorkPolicyFormErrors, WorkPolicyFormValue } from "./hooks/use-work-policy-form";
import {
  ErrorText,
  FormSection,
  InlineChoice,
  TargetOption,
  TargetOptionGrid,
  TargetSelection
} from "./work-type-management.styles";

interface ApprovalPolicySectionProps {
  value: WorkPolicyFormValue;
  errors: WorkPolicyFormErrors;
  onChange: <K extends keyof WorkPolicyFormValue>(
    field: K,
    value: WorkPolicyFormValue[K]
  ) => void;
  searchDepartments: (query: string) => Promise<any[]>;
  searchEmployees: (query: string) => Promise<any[]>;
  confirmTargetChange: (message: string) => boolean;
}

export const ApprovalPolicySection = ({
  value,
  errors,
  onChange,
  searchDepartments,
  searchEmployees,
  confirmTargetChange
}: ApprovalPolicySectionProps) => {
  const targetRefs = useRef<
    Partial<Record<WorkPolicyFormValue["targetType"], HTMLInputElement>>
  >({});

  const changeTarget = (targetType: WorkPolicyFormValue["targetType"]) => {
    const hasSelection =
      value.selectedDepartments.length > 0 || value.selectedEmployees.length > 0;
    if (
      hasSelection &&
      targetType !== value.targetType &&
      !confirmTargetChange("대상 유형을 바꾸면 현재 선택 대상이 제거됩니다. 계속할까요?")
    ) {
      targetRefs.current[value.targetType]?.focus();
      return;
    }
    onChange("targetType", targetType);
    if (targetType !== "dept") onChange("selectedDepartments", []);
    if (targetType !== "user") onChange("selectedEmployees", []);
  };

  return (
    <FormSection>
      <legend>결재 신청 설정</legend>
      <p>이 정책은 결재 신청 화면에서 선택하며 실제 적용기간은 신청 From~To에서 정합니다.</p>
      <div>
        <strong>결재노출 여부</strong>
        <InlineChoice>
          <label>
            <input
              type="radio"
              name="exposureStatus"
              checked={value.exposureStatus === "VISIBLE"}
              onChange={() => onChange("exposureStatus", "VISIBLE")}
            />
            결재에 노출
          </label>
          <label>
            <input
              type="radio"
              name="exposureStatus"
              checked={value.exposureStatus === "HIDDEN"}
              onChange={() => onChange("exposureStatus", "HIDDEN")}
            />
            결재에서 숨김
          </label>
        </InlineChoice>
      </div>
      <div>
        <strong id="work-policy-targetType-label">신청 가능 대상</strong>
        <TargetOptionGrid role="radiogroup" aria-labelledby="work-policy-targetType-label">
          <TargetOption $checked={value.targetType === "all"}>
            <input
              ref={element => {
                if (element) targetRefs.current.all = element;
              }}
              type="radio"
              name="targetType"
              checked={value.targetType === "all"}
              onChange={() => changeTarget("all")}
            />
            전사
          </TargetOption>
          <TargetOption $checked={value.targetType === "dept"}>
            <input
              ref={element => {
                if (element) targetRefs.current.dept = element;
              }}
              type="radio"
              name="targetType"
              checked={value.targetType === "dept"}
              onChange={() => changeTarget("dept")}
            />
            특정 부서
          </TargetOption>
          <TargetOption $checked={value.targetType === "user"}>
            <input
              ref={element => {
                if (element) targetRefs.current.user = element;
              }}
              type="radio"
              name="targetType"
              checked={value.targetType === "user"}
              onChange={() => changeTarget("user")}
            />
            특정 사용자
          </TargetOption>
        </TargetOptionGrid>
      </div>
      {value.targetType === "dept" && (
        <TargetSelection id="work-policy-selectedDepartments">
          {value.selectedDepartments.length === 0 && <p>선택된 부서가 없습니다.</p>}
          <AsyncEntityPicker
            label="부서"
            variant="department"
            searchEntities={searchDepartments}
            selected={value.selectedDepartments}
            onSelectionChange={selected => onChange("selectedDepartments", selected)}
            presentation="dialog"
            previewLimit={5}
            searchPlaceholder="예: 개발, IT본부, 인사팀"
            dialogTitle="부서 검색/선택"
            dialogDescription="전체 조직 경로를 확인해 대상 부서를 선택하세요. 선택 추가 전에는 편집 내용이 바뀌지 않습니다."
            ariaDescribedBy={
              errors.selectedDepartments ? "work-policy-selectedDepartments-error" : undefined
            }
          />
          {errors.selectedDepartments && (
            <ErrorText id="work-policy-selectedDepartments-error" role="alert">
              {errors.selectedDepartments}
            </ErrorText>
          )}
        </TargetSelection>
      )}
      {value.targetType === "user" && (
        <TargetSelection id="work-policy-selectedEmployees">
          {value.selectedEmployees.length === 0 && <p>선택된 사용자가 없습니다.</p>}
          <AsyncEntityPicker
            label="사용자"
            variant="employee"
            searchEntities={searchEmployees}
            selected={value.selectedEmployees}
            onSelectionChange={selected => onChange("selectedEmployees", selected)}
            presentation="dialog"
            previewLimit={5}
            searchPlaceholder="예: 김선준, 100234"
            dialogTitle="사용자 검색/선택"
            dialogDescription="이름·사번·소속 부서를 확인해 동명이인을 구분하세요. 선택 추가 전에는 편집 내용이 바뀌지 않습니다."
            ariaDescribedBy={
              errors.selectedEmployees ? "work-policy-selectedEmployees-error" : undefined
            }
          />
          {errors.selectedEmployees && (
            <ErrorText id="work-policy-selectedEmployees-error" role="alert">
              {errors.selectedEmployees}
            </ErrorText>
          )}
        </TargetSelection>
      )}
      <p>선택한 신청 가능 대상만 결재 화면에서 이 근무정책을 사용할 수 있습니다.</p>
    </FormSection>
  );
};
